import { Pagination } from "@dash/search";
import { ErrorScreen } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";
import { Uuid } from "@megaron/uuid";

import { CubeCollapsedTileSkeletonStack } from "./CubeCollapsedTileSkeletonStack";
import { cubeFactTablePageSize } from "./cubeFactTableLayout";

import type { UnassignedProRow } from "./unassignedProFactsData";

const pageSize = cubeFactTablePageSize;

const firstColumnCellPadding: CSSObject = { padding: "0.75rem" };

type Props = {
  rows: UnassignedProRow[];
  error: Error | null;
  isLoading: boolean;
};

export const UnassignedProCubeTable: React.FC<Props> = ({ rows, error, isLoading }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  if (error) {
    return <ErrorScreen>{error.message}</ErrorScreen>;
  }
  if (isLoading) {
    return <CubeCollapsedTileSkeletonStack />;
  }
  if (rows.length === 0) {
    return <div css={{ color: theme.colors.secondaryText, fontSize: "0.875rem" }}>Brak danych.</div>;
  }

  const tableColumns: TableHeadColumn<string>[] = [
    { label: "Nazwa", isSortable: false },
    { label: "Data dołączenia", isSortable: false },
  ];

  const sliceStart = page * pageSize;
  const pageRows = rows.slice(sliceStart, sliceStart + pageSize);
  const paddedRows: (UnassignedProRow | null)[] = [...pageRows];
  while (paddedRows.length < pageSize) {
    paddedRows.push(null);
  }

  const bodyRows = paddedRows.map((row, index) => {
    if (row === null) {
      return {
        id: `pad-${sliceStart}-${index}`,
        cellsContent: [
          {
            element: <span css={firstColumnCellPadding}>{"\u00a0"}</span>,
          },
          { element: "\u00a0" },
        ],
      };
    }
    const uid = row.uuid.trim();
    const label = row.name;
    const nameElement =
      uid.length > 0 ? (
        <Link
          to={getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id: Uuid(uid) })}
          css={{
            color: theme.colors.primary,
            fontWeight: 500,
            textDecoration: "none",
            ":hover": { textDecoration: "underline" },
          }}
        >
          {label}
        </Link>
      ) : (
        label
      );
    const nameCell = <span css={firstColumnCellPadding}>{nameElement}</span>;
    const cellsContent: RowCellContent[] = [{ element: nameCell }, { element: row.joinedLabel }];
    return {
      id: row.uuid.length > 0 ? row.uuid : `row-${sliceStart + index}`,
      cellsContent,
    };
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Table columns={tableColumns}>
        <TableBody isLoading={false} rows={bodyRows} />
      </Table>
      <Pagination
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        itemCount={rows.length}
      />
    </div>
  );
};
